import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import type { PeriodoValor } from '@/shared/types/filtroPeriodo';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearNegociacaoPeriodoPreview, selectNegociacaoPeriodoPreview } from '../store';
import { type FetchNegociacaoCasosInput, fetchNegociacaoPeriodoPreview } from '../store/actions';
import type { NegociacaoPeriodoCampo, NegociacaoPeriodoParams } from '../types';
import { campoDoPeriodoAtivo, escreverPeriodoNaUrl, lerPeriodoDaUrl, paraParametrosApi, PERIODO_NEGOCIACAO_VAZIO, resumoDaPrevia } from '../utils/negociacoesPeriodo';
const PREVIA_DEBOUNCE_MS = 300;
interface UseNegociacoesPeriodoParams {
  filtrosDaLista: FetchNegociacaoCasosInput;
}
interface UseNegociacoesPeriodoReturn {
  periodo: PeriodoValor;
  parametrosApi: NegociacaoPeriodoParams;
  campoAtivo: NegociacaoPeriodoCampo | null;
  aplicarPeriodo: (valor: PeriodoValor) => void;
  removerPeriodo: () => void;
  atualizarRascunho: (valor: PeriodoValor | null) => void;
  resumoPrevia: string | undefined;
}
export const useNegociacoesPeriodo = t0 => {
  const $ = _c(30);
  const {
    filtrosDaLista
  } = t0;
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const previa = useAppSelector(selectNegociacaoPeriodoPreview);
  const [rascunho, setRascunho] = useState(null);
  const rascunhoDebounced = useDebouncedValue(rascunho, PREVIA_DEBOUNCE_MS);
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.toString();
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryAtual = t1;
  let t2;
  if ($[2] !== queryAtual) {
    t2 = lerPeriodoDaUrl(new URLSearchParams(queryAtual));
    $[2] = queryAtual;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const periodo = t2;
  let t3;
  if ($[4] !== periodo) {
    t3 = paraParametrosApi(periodo);
    $[4] = periodo;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const parametrosApi = t3;
  let t4;
  if ($[6] !== pathname || $[7] !== queryAtual || $[8] !== router) {
    t4 = valor => {
      const proxima = escreverPeriodoNaUrl(new URLSearchParams(queryAtual), valor).toString();
      if (proxima === queryAtual) {
        return;
      }
      router.push(proxima ? `${pathname}?${proxima}` : pathname, {
        scroll: false
      });
    };
    $[6] = pathname;
    $[7] = queryAtual;
    $[8] = router;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const aplicarPeriodo = t4;
  let t5;
  if ($[10] !== aplicarPeriodo) {
    t5 = () => aplicarPeriodo(PERIODO_NEGOCIACAO_VAZIO);
    $[10] = aplicarPeriodo;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const removerPeriodo = t5;
  let t6;
  let t7;
  if ($[12] !== dispatch || $[13] !== filtrosDaLista || $[14] !== rascunhoDebounced) {
    t6 = () => {
      if (!rascunhoDebounced?.inicio) {
        dispatch(clearNegociacaoPeriodoPreview());
        return;
      }
      dispatch(fetchNegociacaoPeriodoPreview({
        ...filtrosDaLista,
        ...paraParametrosApi(rascunhoDebounced)
      }));
    };
    t7 = [dispatch, filtrosDaLista, rascunhoDebounced];
    $[12] = dispatch;
    $[13] = filtrosDaLista;
    $[14] = rascunhoDebounced;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useEffect(t6, t7);
  let t8;
  if ($[17] !== periodo) {
    t8 = campoDoPeriodoAtivo(periodo);
    $[17] = periodo;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== previa || $[20] !== rascunho || $[21] !== rascunhoDebounced) {
    t9 = rascunho?.inicio ? resumoDaPrevia(previa.total, previa.loading || rascunho !== rascunhoDebounced) : undefined;
    $[19] = previa;
    $[20] = rascunho;
    $[21] = rascunhoDebounced;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== aplicarPeriodo || $[24] !== parametrosApi || $[25] !== periodo || $[26] !== removerPeriodo || $[27] !== t8 || $[28] !== t9) {
    t10 = {
      periodo,
      parametrosApi,
      campoAtivo: t8,
      aplicarPeriodo,
      removerPeriodo,
      atualizarRascunho: setRascunho,
      resumoPrevia: t9
    };
    $[23] = aplicarPeriodo;
    $[24] = parametrosApi;
    $[25] = periodo;
    $[26] = removerPeriodo;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
};